import { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Video, Plus, Play, Trash2, ExternalLink, Youtube, Film, Instagram } from "lucide-react";
import { toast } from "sonner";

const PROVIDER_META = {
  youtube:   { label: "YouTube",   Icon: Youtube,   color: "text-red-400 border-red-500/30" },
  vimeo:     { label: "Vimeo",     Icon: Film,      color: "text-blue-400 border-blue-500/30" },
  hudl:      { label: "Hudl",      Icon: Film,      color: "text-orange-400 border-orange-500/30" },
  instagram: { label: "Instagram", Icon: Instagram, color: "text-pink-400 border-pink-500/30" },
  other:     { label: "Video",     Icon: Video,     color: "text-ink-300 border-ink-600" },
};

export default function PlayerHighlights({ slug, uploadCode }) {
  const { user } = useAuth();
  const [videos, setVideos] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [url, setUrl] = useState("");
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const [playing, setPlaying] = useState(null); // { embed_url, url, provider, title }

  const canAdd = !!user || !!uploadCode;
  const isCoach = !!user;

  const load = async () => {
    try {
      const { data } = await api.get(`/players/${slug}/videos`);
      setVideos(data);
    } catch { /* silent */ }
  };
  useEffect(() => { if (slug) load(); }, [slug]);

  const submit = async (e) => {
    e.preventDefault();
    if (!url.trim()) { toast.error("Paste a video URL"); return; }
    setBusy(true);
    try {
      const payload = { url: url.trim(), title: title.trim() || null };
      if (!isCoach) payload.upload_code = (uploadCode || "").trim().toUpperCase();
      await api.post(`/players/${slug}/videos`, payload);
      toast.success("Highlight added");
      setAddOpen(false); setUrl(""); setTitle("");
      load();
    } catch (err) { toast.error(formatError(err)); }
    finally { setBusy(false); }
  };

  const remove = async (v) => {
    if (!confirm(`Remove "${v.title}" from ${slug}'s highlights?`)) return;
    try {
      await api.delete(`/players/${slug}/videos/${v.id}`);
      toast.success("Highlight removed");
      load();
    } catch (err) { toast.error(formatError(err)); }
  };

  return (
    <section className="bg-ink-800 rounded-lg border border-white/5 p-6" data-testid="highlights-section">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="heading-3 flex items-center gap-2">
          <Video className="w-5 h-5 text-burgundy" /> Highlight Reel
        </h3>
        {canAdd && (
          <Button
            onClick={() => setAddOpen(true)}
            className="bg-burgundy hover:bg-burgundy-hover"
            size="sm"
            data-testid="add-highlight-btn"
          >
            <Plus className="w-4 h-4 mr-1" /> Add
          </Button>
        )}
      </div>

      {videos.length === 0 ? (
        <div className="text-center py-10 text-ink-400">
          <Film className="w-8 h-8 mx-auto mb-2 text-ink-500" />
          <p className="text-sm">No highlights posted yet.</p>
          {canAdd && <p className="text-xs mt-1">Paste a YouTube, Vimeo, Hudl, or Instagram link to get started.</p>}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {videos.map((v) => {
            const meta = PROVIDER_META[v.provider] || PROVIDER_META.other;
            const canPlay = !!v.embed_url;
            return (
              <div key={v.id} className="group relative bg-ink-900 rounded-lg overflow-hidden border border-ink-700/60 hover:border-burgundy/60 transition" data-testid={`video-card-${v.id}`}>
                {/* Thumbnail */}
                <button
                  onClick={() => canPlay ? setPlaying(v) : window.open(v.url, "_blank", "noopener,noreferrer")}
                  className="relative w-full aspect-video block bg-ink-950 overflow-hidden"
                  data-testid={`play-video-${v.id}`}
                >
                  {v.thumbnail_url ? (
                    <img src={v.thumbnail_url} alt={v.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-ink-900 to-burgundy/30">
                      <meta.Icon className="w-10 h-10 text-ink-400" />
                    </div>
                  )}
                  <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition">
                    {canPlay ? (
                      <div className="w-14 h-14 rounded-full bg-burgundy/90 flex items-center justify-center">
                        <Play className="w-6 h-6 text-white ml-1" fill="currentColor" />
                      </div>
                    ) : (
                      <ExternalLink className="w-8 h-8 text-white" />
                    )}
                  </div>
                </button>

                <div className="p-3 flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="font-display font-bold uppercase text-white text-sm leading-tight truncate">{v.title}</p>
                    <div className="flex items-center gap-1.5 mt-1">
                      <span className={`inline-flex items-center gap-1 text-[9px] uppercase tracking-wider font-bold border rounded px-1.5 py-0.5 ${meta.color}`}>
                        <meta.Icon className="w-2.5 h-2.5" /> {meta.label}
                      </span>
                    </div>
                  </div>
                  {isCoach && (
                    <button
                      onClick={() => remove(v)}
                      className="text-ink-400 hover:text-red-400 p-1 rounded transition shrink-0"
                      title="Remove highlight"
                      data-testid={`delete-video-${v.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add-video dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="bg-ink-900 border-ink-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="heading-3 flex items-center gap-2">
              <Video className="w-5 h-5 text-burgundy" /> Add a Highlight
            </DialogTitle>
            <DialogDescription className="text-ink-300">
              Paste your video URL from YouTube, Vimeo, Hudl, or Instagram. It&apos;ll appear on your public profile.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4" data-testid="add-highlight-form">
            <div>
              <Label className="eyebrow">Video URL</Label>
              <Input
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://www.youtube.com/watch?v=..."
                className="bg-ink-800 border-ink-700 mt-2"
                autoFocus
                required
                data-testid="highlight-url-input"
              />
            </div>
            <div>
              <Label className="eyebrow">Title (optional)</Label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g., 2025-26 Season Reel"
                className="bg-ink-800 border-ink-700 mt-2"
                data-testid="highlight-title-input"
              />
            </div>
            <Button type="submit" disabled={busy} className="w-full bg-burgundy hover:bg-burgundy-hover" data-testid="highlight-submit">
              {busy ? "Adding…" : "Add Highlight"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Player dialog — video embed */}
      <Dialog open={!!playing} onOpenChange={(v) => !v && setPlaying(null)}>
        <DialogContent className="bg-black border-ink-700 max-w-4xl p-0 overflow-hidden">
          <DialogHeader className="px-4 py-3 border-b border-ink-800 bg-ink-900">
            <DialogTitle className="text-white font-display uppercase tracking-wide">{playing?.title}</DialogTitle>
            <DialogDescription className="text-ink-400 text-xs">
              {playing?.provider ? `Playing from ${playing.provider}` : "Video playback"}
            </DialogDescription>
          </DialogHeader>
          {playing?.embed_url && (
            <div className="aspect-video bg-black">
              <iframe
                src={playing.embed_url}
                title={playing.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                className="w-full h-full border-0"
                data-testid="video-player-iframe"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
